import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, take } from 'rxjs';
import { VerwaltungBereich } from './verwaltung-bereich';

/**
 * Leiste „Ungespeicherte Änderungen“ eines Verwaltungsbereichs (Design app-pages.jsx, Einstellungen).
 * Sie allein meldet das Ergebnis des Speicherns; schließt `speichern()` ohne Wert ab, steht die
 * Meldung beim Bereich und die Leiste bleibt.
 */
@Component({
  selector: 'app-vw-ungespeichert',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    @if (bereich().hatUngespeicherteAenderungen()) {
      <div class="vw-ungespeichert" role="status" data-testid="vw-ungespeichert">
        <span class="vw-ungespeichert__hinweis">{{ 'verwaltung.ungespeichert.hinweis' | translate }}</span>
        <button
          type="button"
          class="vw-ungespeichert__knopf vw-ungespeichert__knopf--verwerfen"
          data-testid="vw-ungespeichert-verwerfen"
          (click)="bereich().verwerfen()"
        >
          {{ 'verwaltung.ungespeichert.verwerfen' | translate }}
        </button>
        <button
          type="button"
          class="vw-ungespeichert__knopf vw-ungespeichert__knopf--speichern"
          data-testid="vw-ungespeichert-speichern"
          [disabled]="!bereich().kannSpeichern() || speichert()"
          (click)="speichern()"
        >
          {{ 'verwaltung.ungespeichert.speichern' | translate }}
        </button>
      </div>
    }
  `,
  styles: `
    .vw-ungespeichert {
      position: fixed;
      bottom: 1.5rem;
      left: 50%;
      transform: translateX(-50%);
      z-index: 30;
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 0.875rem 1.375rem;
      border-radius: 0.75rem;
      background: var(--to-text-strong);
      color: var(--to-card);
      box-shadow: var(--to-shadow-raised);
      font-size: 0.875rem;
      white-space: nowrap;
    }

    .vw-ungespeichert__hinweis {
      flex: 1;
    }

    .vw-ungespeichert__knopf {
      padding: 0.5rem 0.875rem;
      border: none;
      border-radius: 0.5rem;
      font-family: inherit;
      font-size: 0.8125rem;
      color: var(--to-card);
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid var(--to-focus-ring);
        outline-offset: 2px;
      }

      &--verwerfen {
        background: rgba(255, 255, 255, 0.15);

        &:hover {
          background: rgba(255, 255, 255, 0.25);
        }
      }

      &--speichern {
        background: var(--color-brand-accent);
        font-weight: 600;

        &:hover:not(:disabled) {
          background: var(--to-brand-deep);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }

    @media (max-width: 767.98px) {
      .vw-ungespeichert {
        left: 1rem;
        right: 1rem;
        transform: none;
        white-space: normal;
      }
    }
  `,
})
export class VwUngespeichertComponent {
  readonly bereich = input.required<VerwaltungBereich>();

  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly speichert = signal(false);

  protected speichern(): void {
    if (this.speichert()) return;
    this.speichert.set(true);
    this.bereich()
      .speichern()
      .pipe(
        take(1),
        finalize(() => this.speichert.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.melden('settings.saveSuccess', 'snackbar-success'),
        error: () => this.melden('settings.saveError', 'snackbar-error'),
      });
  }

  private melden(schluessel: string, panelClass: string): void {
    this.snackBar.open(this.translate.instant(schluessel), undefined, {
      duration: 3000,
      verticalPosition: 'top',
      panelClass,
    });
  }
}
